import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../auth/AuthProvider";
import { useGoals, useProfiles } from "../goals/api";
import { useFeed, usePendingCheckIns } from "../checkins/api";
import { CheckInRow, PendingRow } from "./CheckInRow";
import { NotificationsCard } from "../nudges/NotificationsCard";
import { Ring } from "../../components/Ring";
import { ProfileAvatar } from "../../components/ProfileAvatar";
import { EmptyState, Skeleton } from "../../components/ui";

export function FeedPage() {
  const { userId, profile } = useAuth();
  const goals = useGoals();
  const profiles = useProfiles();
  const feed = useFeed();
  const pending = usePendingCheckIns();

  // You first, then everyone else by progress, highest first.
  const standing = (profiles.data ?? []).map((person) => {
    const theirs = (goals.data ?? []).filter(
      (goal) => goal.owner_id === person.id && !goal.is_archived,
    );
    const done = theirs.filter((goal) => goal.stats?.period_complete).length;
    return { person, done, total: theirs.length, progress: theirs.length ? done / theirs.length : 0 };
  });
  const people = [
    ...standing.filter((row) => row.person.id === userId),
    ...standing
      .filter((row) => row.person.id !== userId)
      .sort((a, b) => b.progress - a.progress),
  ];

  return (
    <div className="-mx-5 flex flex-col gap-[18px] pt-safe text-[#0a0a0b]">
      <header className="flex items-center justify-between px-5 pt-3">
        <h1 className="font-[inherit] text-[22px] font-semibold lowercase tracking-[-0.03em]">
          our pact
        </h1>
        <span className="font-['Geist_Mono',ui-monospace,monospace] text-xs text-[#8a8a94]">
          {format(new Date(), "EEE · MMM d")}
        </span>
      </header>

      <div className="px-5 empty:hidden">
        <NotificationsCard />
      </div>

      {/* ---- where everyone stands today ---- */}
      <section aria-label="Today's progress">
        {profiles.isLoading || goals.isLoading ? (
          <div className="flex gap-3.5 px-5">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-[84px] w-14 !rounded-2xl" />
            ))}
          </div>
        ) : (
          <ul className="flex snap-x gap-3.5 overflow-x-auto px-5 no-scrollbar">
            {people.map(({ person, done, total, progress }) => (
              <li key={person.id} className="shrink-0 snap-start">
                <Link
                  to={`/people/${person.id}`}
                  className="flex w-14 flex-col items-center gap-1.5 text-[#0a0a0b] no-underline"
                >
                  <Ring fraction={progress} size={56} stroke={3}>
                    <ProfileAvatar
                      name={person.display_name}
                      avatarPath={person.avatar_path}
                      size={40}
                      ring={0}
                    />
                  </Ring>
                  <span className="max-w-full truncate text-xs font-medium">
                    {person.id === userId ? "You" : person.display_name}
                  </span>
                  <span className="-mt-1 font-['Geist_Mono',ui-monospace,monospace] text-[11px] text-[#8a8a94]">
                    {done}/{total}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- what actually happened ---- */}
      <section aria-label="Recent check-ins" className="px-4">
        {pending.length > 0 && (
          <ul className="mb-3 flex flex-col gap-3">
            {pending.map((entry) => (
              <li key={entry.id}>
                <PendingRow entry={entry} profile={profile} />
              </li>
            ))}
          </ul>
        )}

        {feed.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        ) : feed.isError ? (
          <EmptyState title="Couldn't load the feed. Pull to refresh, or try again in a moment." />
        ) : feed.data?.length ? (
          <ul className="flex flex-col gap-3">
            {feed.data.map((entry) => (
              <li key={entry.id}>
                <CheckInRow entry={entry} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="Nothing yet. When someone checks in, it'll show up here."
            action={
              <Link to="/goals" className="btn btn-secondary no-underline">
                see your goals
              </Link>
            }
          />
        )}
      </section>
    </div>
  );
}
